import { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, AuthErrorCodes } from 'firebase/auth';
import RecoverPassword from './RecoverPassword';
import CreateAccount from './createAccount/CreateAccount';
import { CreateAccountProvider } from './createAccount/Context';
import { useAppContext } from '../contexts/AppContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { appId, brandingEstabId } from '../branding';
import { urlFile } from '../AxiosConfig';
import { EstabelecimentosService } from '../modules/estabelecimentos/system-admin/Service';
import { getAuthErrorMessage, LoginService } from './Service';

// --- Assets ---
import { motion } from 'framer-motion';
import {
    ArrowRight,
    CalendarCheck,
    CheckCircle2,
    LockKeyhole,
    Mail,
    ShieldCheck,
    Sparkles,
    Star,
    TrendingUp,
} from 'lucide-react';

const loginService = new LoginService();
const estabelecimentosService = new EstabelecimentosService();

const salonImages = [
    '/login/image1.jpg',
    '/login/image2.jpeg',
    '/login/image3.jpg',
    '/login/image4.jpg',
    '/login/image5.jpg',
];

const loginStats = [
    { icon: CalendarCheck, value: '12k+', label: 'agendamentos/mês' },
    { icon: TrendingUp, value: '98%', label: 'satisfação' },
    { icon: Star, value: '4.9', label: 'avaliação média' },
];

const loginFeatures = [
    'Agenda inteligente',
    'Página pública',
    'Planos e assinaturas',
    'Financeiro integrado',
];

export default function LoginPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingImage, setLoadingImage] = useState(true);

    const [searchParams] = useSearchParams();
    const estabIdFromURL = searchParams.get('estabId');
    const clientOnly = Boolean(estabIdFromURL);
    // A marca do deployment vale mesmo sem origem na URL; o modo cliente continua exigindo ?estabId=.
    const brandEstabId = estabIdFromURL || brandingEstabId;
    const pageSlug = searchParams.get('page') || '';
    const establishmentPage = clientOnly && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(pageSlug)
        ? `/page/${encodeURIComponent(pageSlug)}` : '';
    const signupIntent = !clientOnly && searchParams.get('signup') === 'estab';
    const [estabName, setEstabName] = useState('');

    const [estabId, setEstabId] = useState('');
    const [recoverPass, setRecoverPass] = useState(false);
    const [createAccount, setCreateAccount] = useState(signupIntent);
    const [createAccountTab, setCreateAccountTab] = useState<'client' | 'estab'>(signupIntent ? 'estab' : 'client');
    const { image, theme, errorSnackbar } = useAppContext();
    const [imageEstab, setImageEstab] = useState('');
    const defaultLogoSrc = theme === 'dark' ? '/logo-light.png' : '/logo.png';
    const [logoSrc, setLogoSrc] = useState(defaultLogoSrc);
    const [logoVisible, setLogoVisible] = useState(true);
    const [currentImage, setCurrentImage] = useState(0);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImage((prev) => (prev + 1) % salonImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setEstabId(estabIdFromURL || '');
        setImageEstab('');
        setEstabName('');
        if (brandEstabId) {
            setLoadingImage(true);
            estabelecimentosService.getInfoById(brandEstabId)
                .then(({ data }) => {
                    if (cancelled) return;
                    setEstabName(data?.name || '');
                    document.title = data?.name ? `${data.name} - Login` : 'SalonFlow - Login';
                    setImageEstab(data?.imageId ? `${urlFile}/${data.imageId}` : '');
                })
                .catch(() => { if (!cancelled) errorSnackbar('Não foi possível carregar a marca do estabelecimento.'); })
                .finally(() => { if (!cancelled) setLoadingImage(false); });
        } else {
            document.title = 'SalonFlow - Login';
            setLoadingImage(false);
        }
        return () => { cancelled = true; };
    }, [estabIdFromURL, brandEstabId]);

    useEffect(() => {
        if (signupIntent) {
            setCreateAccount(true);
            setCreateAccountTab(clientOnly ? 'client' : 'estab');
        }
    }, [signupIntent]);

    useEffect(() => {
        const contextLogo = image === `${appId}/logo.png` ? '' : image;
        const nextLogo = imageEstab || contextLogo || defaultLogoSrc;
        setLogoSrc(nextLogo);
        setLogoVisible(true);
    }, [defaultLogoSrc, imageEstab, image]);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            await loginService.after(estabIdFromURL || undefined);
            if (estabIdFromURL && auth.currentUser) {
                try { localStorage.setItem(`sf:client-estab:${auth.currentUser.uid}`, estabIdFromURL); } catch {}
            }
            navigate('/');
            window.location.reload();
        } catch (error: any) {
            if (error?.message?.includes('Unverified email')) {
                error.code = AuthErrorCodes.UNVERIFIED_EMAIL;
            }
            errorSnackbar(error?.response?.data?.message || getAuthErrorMessage(error?.code || ''));
        } finally {
            setLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        setLoading(true);
        try {
            await signInWithPopup(auth, provider);
            await loginService.after(estabIdFromURL || undefined);
            if (estabIdFromURL && auth.currentUser) {
                try { localStorage.setItem(`sf:client-estab:${auth.currentUser.uid}`, estabIdFromURL); } catch {}
            }
            navigate('/');
            window.location.reload();
        } catch (error: any) {
            errorSnackbar(error?.response?.data?.message || getAuthErrorMessage(error?.code || ''));
        } finally { setLoading(false); }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: 'easeOut' as any, // força o TS a aceitar
            },
        },
    };

    const loginLogo = loadingImage ? <span className="loading loading-spinner loading-sm" role="status" aria-label="Carregando logo" /> : brandEstabId && !imageEstab && estabName ? <span className="text-2xl font-semibold text-center">{estabName}</span> : logoVisible ? (
                        <img
                            src={logoSrc}
                            alt={estabName || 'SalonFlow'}
                            className="h-14 max-w-[230px] object-contain"
                            onError={() => setLogoVisible(false)}
                        />
                    ) : (
                        <span className="text-2xl font-semibold tracking-tight">{estabName || 'SalonFlow'}</span>
                    );

    return (
        <main className="flex min-h-screen items-center justify-center bg-base-200 px-5 py-10 text-base-content">
            <div className="w-full max-w-sm">
                <div className="mb-10 flex justify-center">
                    {establishmentPage ? (
                        <Link to={establishmentPage} aria-label={`Voltar à página de ${estabName || 'estabelecimento'}`} className="inline-flex justify-center rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                            {loginLogo}
                        </Link>
                    ) : loginLogo}
                </div>

                {createAccount ? (
                    <CreateAccountProvider setCreateAccount={setCreateAccount} estabId={estabId}>
                        <CreateAccount clientOnly={clientOnly} initialTab={createAccountTab} />
                    </CreateAccountProvider>
                ) : recoverPass ? (
                    <RecoverPassword setRecoverPass={setRecoverPass} />
                ) : (
                    <form onSubmit={onSubmit} className="space-y-5">
                        <div className="space-y-1">
                            <h1 className="text-2xl font-semibold tracking-tight">Entrar</h1>
                            <p className="text-sm text-base-content/60">Use seus dados para acessar sua conta.</p>
                        </div>

                        <label className="form-control w-full">
                            <span className="mb-1.5 text-sm font-medium">E-mail</span>
                            <input
                                type="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                disabled={loading}
                                className="input input-bordered h-12 w-full rounded-xl"
                                autoComplete="email"
                                required
                            />
                        </label>

                        <label className="form-control w-full">
                            <span className="mb-1.5 text-sm font-medium">Senha</span>
                            <input
                                type="password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                disabled={loading}
                                className="input input-bordered h-12 w-full rounded-xl"
                                autoComplete="current-password"
                                required
                            />
                        </label>

                        <button type="button" onClick={() => setRecoverPass(true)} className="text-sm text-primary hover:underline">
                            Esqueceu sua senha?
                        </button>

                        <button type="submit" disabled={loading} className="btn btn-primary h-12 w-full rounded-xl">
                            {loading ? <span className="loading loading-spinner loading-sm" /> : 'Entrar'}
                        </button>

                        <div className="divider text-xs text-base-content/45">ou</div>

                        <button type="button" onClick={signInWithGoogle} disabled={loading} className="btn h-12 w-full rounded-xl border-base-content/15 bg-base-100">
                            <img src="/google-icon.svg" alt="" className="h-5 w-5" />
                            Entrar com Google
                        </button>

                        <p className="pt-2 text-center text-sm text-base-content/60">
                            Não tem uma conta?{' '}
                            <button type="button" onClick={() => { setCreateAccountTab(clientOnly ? 'client' : 'estab'); setCreateAccount(true); }} className="font-medium text-primary hover:underline">
                                Criar conta
                            </button>
                        </p>
                    </form>
                )}
            </div>
        </main>
    );

    return (
        <div
            className="min-h-screen bg-base-200 font-body-modern text-base-content"
        >
            <div className="flex min-h-screen items-start justify-center px-5 pb-8 pt-14 sm:pt-16">
                {false && (
                    <section className="relative hidden min-h-screen overflow-hidden lg:flex">
                        <div className="absolute inset-0 scale-[1.04]">
                            {salonImages.map((src, index) => (
                                <img
                                    key={src}
                                    src={src}
                                    alt="Salão de beleza"
                                    className={`absolute inset-0 h-full w-full object-cover transition-all duration-1000 ease-in-out ${
                                        index === currentImage ? 'opacity-100' : 'opacity-0'
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(17,13,24,0.74)_0%,rgba(17,13,24,0.46)_42%,rgba(17,13,24,0.92)_100%)]" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(245,158,11,0.22),transparent_34%),radial-gradient(circle_at_12%_78%,rgba(192,132,252,0.22),transparent_35%)]" />

                        <div className="relative flex w-full flex-col justify-between p-8 xl:p-12">
                            <div className="flex items-center justify-between gap-4">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-xs font-semibold text-white/85 backdrop-blur-xl">
                                    <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                                    Plataforma completa para salões e barbearias
                                </div>

                                <div className="flex gap-2">
                                    {salonImages.map((src, index) => (
                                        <button
                                            key={`${src}_thumb`}
                                            type="button"
                                            onClick={() => setCurrentImage(index)}
                                            className={`relative h-11 w-11 overflow-hidden rounded-xl border transition ${
                                                currentImage === index
                                                    ? 'border-amber-300 ring-2 ring-amber-300/35'
                                                    : 'border-white/15 hover:border-white/45'
                                            }`}
                                            aria-label={`Selecionar imagem ${index + 1}`}
                                        >
                                            <img src={src} alt={`Preview ${index + 1}`} className="h-full w-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="max-w-2xl pb-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.55 }}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-xs font-medium text-white/80 backdrop-blur-xl"
                                >
                                    <ShieldCheck className="h-3.5 w-3.5 text-amber-300" />
                                    Acesso seguro para sua operação
                                </motion.div>

                                <motion.h1
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.65, delay: 0.05 }}
                                    className="mt-5 max-w-xl text-5xl font-extrabold leading-[0.98] tracking-tight xl:text-6xl"
                                >
                                    A gestão do seu salão,{' '}
                                    <span className="bg-[linear-gradient(90deg,#f59e0b,#fde68a)] bg-clip-text text-transparent">
                                        em outro nível.
                                    </span>
                                </motion.h1>

                                <motion.p
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.65, delay: 0.12 }}
                                    className="mt-5 max-w-lg text-lg leading-relaxed text-slate-200/85"
                                >
                                    Entre para acompanhar agenda, clientes, equipe, vendas e indicadores no mesmo fluxo profissional.
                                </motion.p>

                                <motion.div
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.65, delay: 0.2 }}
                                    className="mt-7 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-slate-200/78"
                                >
                                    {loginFeatures.map((feature) => (
                                        <span key={feature} className="inline-flex items-center gap-1.5">
                                            <CheckCircle2 className="h-3.5 w-3.5 text-amber-300" />
                                            {feature}
                                        </span>
                                    ))}
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.65, delay: 0.28 }}
                                    className="mt-9 grid max-w-xl grid-cols-3 gap-3"
                                >
                                    {loginStats.map((stat) => {
                                        const Icon = stat.icon;
                                        return (
                                            <div
                                                key={stat.label}
                                                className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-xl"
                                            >
                                                <Icon className="mb-3 h-5 w-5 text-amber-300" />
                                                <div className="text-xl font-bold leading-none">{stat.value}</div>
                                                <div className="mt-1 text-[11px] text-slate-300">{stat.label}</div>
                                            </div>
                                        );
                                    })}
                                </motion.div>
                            </div>
                        </div>
                    </section>
                )}

                <section className="flex w-full items-center justify-center">
                    <div className="w-full max-w-[420px] space-y-7">
                        {false && (
                            <div className="relative h-44 overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.05] shadow-2xl shadow-black/30 lg:hidden">
                                <img
                                    src={salonImages[currentImage]}
                                    alt="Ambiente profissional"
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-[#130f1c]/85 via-[#130f1c]/35 to-transparent" />
                                <div className="absolute left-4 bottom-4 right-4">
                                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold text-white/85 backdrop-blur-xl">
                                        <Sparkles className="h-3 w-3 text-amber-300" />
                                        SalonFlow
                                    </div>
                                    <div className="mt-2 text-2xl font-bold leading-tight text-white">
                                        Gestão completa em poucos passos
                                    </div>
                                </div>
                            </div>
                        )}

                        {!loadingImage && (
                            <div className="flex justify-center">
                                {logoVisible ? (
                                    <div
                                        className={`inline-flex max-w-full rounded-2xl px-4 py-3 ${
                                            (logoSrc === '/logo-light.png' || logoSrc === '/logo.png')
                                                ? 'bg-transparent'
                                                : 'border border-white/10 bg-white/[0.92] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.65)]'
                                        }`}
                                    >
                                        <img
                                            src={logoSrc}
                                            alt="Logo"
                                            className={`max-w-[260px] object-contain ${
                                                (logoSrc === '/logo-light.png' || logoSrc === '/logo.png')
                                                    ? 'h-16 md:h-[4.5rem]'
                                                    : 'h-12 md:h-14'
                                            }`}
                                            onError={() => {
                                                if (logoSrc !== defaultLogoSrc) {
                                                    setLogoSrc(defaultLogoSrc);
                                                    return;
                                                }
                                                setLogoVisible(false);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="rounded-2xl border border-white/10 bg-white/[0.06] px-5 py-3 text-lg font-bold text-white backdrop-blur-xl">
                                        Salon<span className="text-amber-300">Flow</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="px-2 py-1 md:px-4">
                            {createAccount ? (
                                <CreateAccountProvider setCreateAccount={setCreateAccount} estabId={estabId}>
                                    <CreateAccount clientOnly={clientOnly} initialTab={createAccountTab} />
                                </CreateAccountProvider>
                            ) : recoverPass ? (
                                <RecoverPassword setRecoverPass={setRecoverPass} />
                            ) : (
                                <>
                                    <div className="mb-6 text-center md:text-left">
                                        <h2 className="text-2xl font-semibold tracking-tight text-base-content">Acesse sua conta</h2>
                                        <p className="mt-2 text-sm leading-relaxed text-base-content/60">
                                            Bem-vindo de volta. Entre para continuar sua operação.
                                        </p>
                                    </div>

                                    <motion.form
                                        onSubmit={(event) => {
                                            const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
                                            if (submitter && submitter.dataset.loginSubmit !== 'true') {
                                                event.preventDefault();
                                                return;
                                            }
                                            onSubmit(event);
                                        }}
                                        className="space-y-5"
                                        variants={containerVariants}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        <motion.div className="relative" variants={itemVariants}>
                                            <Mail className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                disabled={loading}
                                                className="input input-bordered h-12 w-full rounded-xl border-base-content/10 bg-base-100 pl-12 pr-4 text-base-content placeholder:text-base-content/35 focus:border-primary focus:outline-none"
                                                placeholder="seuemail@exemplo.com"
                                                required
                                            />
                                        </motion.div>

                                        <motion.div className="relative" variants={itemVariants}>
                                            <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                disabled={loading}
                                                className="input input-bordered h-12 w-full rounded-xl border-base-content/10 bg-base-100 pl-12 pr-4 text-base-content placeholder:text-base-content/35 focus:border-primary focus:outline-none"
                                                placeholder="Sua senha"
                                                required
                                            />
                                        </motion.div>

                                        <motion.div className="flex justify-end" variants={itemVariants}>
                                            <button
                                                type="button"
                                                onClick={() => setRecoverPass(true)}
                                                className="text-sm font-medium text-primary transition hover:opacity-75"
                                            >
                                                Esqueceu sua senha?
                                            </button>
                                        </motion.div>

                                        <motion.button
                                            type="submit"
                                            data-login-submit="true"
                                            disabled={loading}
                                            className="btn btn-primary h-12 w-full rounded-xl border-0 px-5 font-semibold shadow-none disabled:cursor-not-allowed disabled:opacity-70"
                                            variants={itemVariants}
                                        >
                                            <span className="absolute inset-0 bg-white/15 opacity-0 transition group-hover:opacity-100" />
                                            {loading ? (
                                                <span className="loading loading-spinner loading-sm" />
                                            ) : (
                                                <>
                                                    Entrar
                                                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                                                </>
                                            )}
                                        </motion.button>

                                        <div className="flex items-center">
                                            <div className="h-px flex-grow bg-white/10" />
                                            <span className="mx-4 text-xs font-semibold text-slate-500">OU</span>
                                            <div className="h-px flex-grow bg-white/10" />
                                        </div>

                                        <button
                                            type="button"
                                            onClick={signInWithGoogle}
                                            disabled={loading}
                                            className="btn h-12 w-full rounded-xl border-base-content/10 bg-base-100 px-4 text-sm font-semibold text-base-content hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-70"
                                        >
                                            <img src="/google-icon.svg" alt="Google" className="mr-3 h-5 w-5" />
                                            Entrar com Google
                                        </button>
                                    </motion.form>

                                    <p className="mt-7 text-center text-sm text-base-content/60">
                                        Não tem uma conta?{' '}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setCreateAccountTab(clientOnly ? 'client' : 'estab');
                                                setCreateAccount(true);
                                            }}
                                            className="font-semibold text-primary transition hover:opacity-75"
                                        >
                                            Comece o teste grátis
                                        </button>
                                    </p>
                                </>
                            )}
                        </div>

                    </div>
                </section>
            </div>
        </div>
    );
}
